import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Settings as SettingsIcon, ArrowUp, ArrowDown, Sliders, Lock, Calculator, Clock, Gift, FileText, Wrench, Cog, Scale, Mail, Building2, Users, Newspaper, CreditCard, Volume2, ChevronRight, Database, RefreshCw, AlertTriangle, Layout, Bell } from 'lucide-react';
import AdminConfiguration from './AdminConfiguration';
import WorkingHoursEditor from './WorkingHoursEditor';
import BonusPackagesEditor from './BonusPackagesEditor';
import ConfigTextsEditor from './ConfigTextsEditor';
import ReadingMethodsEditor from './ReadingMethodsEditor';
import GearboxTypesEditor from './GearboxTypesEditor';
import OriginalFilesLayoutEditor from './OriginalFilesLayoutEditor';
import LegalDocsEditor from './LegalDocsEditor';
import EmailTemplatesEditor from './EmailTemplatesEditor';
import CompaniesEditor from './CompaniesEditor';
import RechargeLimitsEditor from './RechargeLimitsEditor';
import CustomerGroupsEditor from './CustomerGroupsEditor';
import ServiceFlagsEditor from './ServiceFlagsEditor';
import PriceHoursEditor from './PriceHoursEditor';
import PinCodesEditor from './PinCodesEditor';
import AdminNewsEditor from './AdminNewsEditor';
import StripeSettingsEditor from './StripeSettingsEditor';
import LoginBannerEditor from './admin/LoginBannerEditor';
import SignupEmailTipEditor from './admin/SignupEmailTipEditor';
import WelcomeBonusEditor from './WelcomeBonusEditor';
import CustomerBadgeSettings from './admin/CustomerBadgeSettings';
import SecurityBlocklistPanel from './admin/SecurityBlocklistPanel';
import RiskLimitsPanel from './admin/RiskLimitsPanel';
import SoundsEditor from './SoundsEditor';
import InstructionsLibraryEditor from './InstructionsLibraryEditor';
import OriginalFilesAdmin from './OriginalFilesAdmin';
import AdminParser from './AdminParser';
import AdminMaintenance from './AdminMaintenance';
import AccessTimeEditor from './AccessTimeEditor';
import TicketSlaAlertEditor from './settings/TicketSlaAlertEditor';
import InvoiceTemplateEditor from './InvoiceTemplateEditor';
import EmailSenderEditor from './EmailSenderEditor';
import AvatarUploadCard from './AvatarUploadCard';
import { User as UserIcon } from 'lucide-react';
import PriceMultiplierEditor from './PriceMultiplierEditor';
import CaptchaSettingsEditor from './CaptchaSettingsEditor';
import CaptchaStats from './CaptchaStats';
import { Shield } from 'lucide-react';
import HptProxySettings from './admin/HptProxySettings';
import DataToolsSettings from './admin/DataToolsSettings';



// Sidebar layout — groups related settings into collapsible sections.
// Each entry: { id, label, icon }. Reordering is allowed; the first entry
// of the first group is the default landing tab.
const SETTINGS_GROUPS = [
  {
    id: 'account',
    label: 'My account',
    items: [
      { id: 'profile-photo', label: 'Profile photo', icon: UserIcon },
      { id: 'my-password', label: 'Password', icon: Lock },
    ],
  },
  {
    id: 'tuning_pricing',
    label: 'Tuning & Pricing',
    items: [
      { id: 'tuning',          label: 'Tuning options',     icon: Sliders },
      { id: 'configuration',   label: 'Pricing rules',      icon: Calculator },
      { id: 'price-multiplier', label: 'Global price multiplier', icon: Sliders },
      { id: 'price-hours',     label: 'Service hours',      icon: Clock },
      { id: 'reading-methods', label: 'Reading methods',    icon: Wrench },
      { id: 'gearbox-types',   label: 'Gearbox types',       icon: Cog },
      { id: 'disable',         label: 'Service flags',      icon: EyeOff },
      { id: 'pins',            label: 'PIN codes',          icon: Lock },
    ],
  },
  {
    id: 'payments',
    label: 'Payments & Bonuses',
    items: [
      { id: 'recharge-limits', label: 'Recharge limits',   icon: CreditCard },
      { id: 'bonus',         label: 'Recharge bonuses',  icon: Gift },
      { id: 'welcome-bonus', label: 'Welcome bonus',     icon: Gift },
      { id: 'stripe',        label: 'Stripe',            icon: CreditCard },
      { id: 'invoice-template', label: 'Invoice template', icon: FileText },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    items: [
      { id: 'email-sender', label: 'Email sender', icon: Mail },
      { id: 'email',  label: 'Email templates', icon: Mail },
      { id: 'sounds', label: 'Sounds',          icon: Volume2 },
      { id: 'news',   label: 'News bell',       icon: Newspaper },
      { id: 'texts',  label: 'Config texts',    icon: FileText },
      { id: 'login-banner', label: 'Login page banner', icon: Newspaper },
      { id: 'signup-tip',   label: 'Signup email tip',    icon: Mail },
    ],
  },
  {
    id: 'customers',
    label: 'Customers & Schedule',
    items: [
      { id: 'companies',     label: 'Companies',        icon: Building2 },
      { id: 'customer-badges', label: 'Customer badges', icon: Building2 },
      { id: 'security-blocklist', label: 'Blocked e-mails', icon: Building2 },
      { id: 'risk-limits', label: 'Limits & anti-abuse', icon: Shield },
      { id: 'groups',        label: 'Customer groups',  icon: Users },
      { id: 'working-hours', label: 'Working hours',    icon: Clock },
    ],
  },
  {
    id: 'legal',
    label: 'Content',
    items: [
      { id: 'legal', label: 'Legal documents', icon: Scale },
      { id: 'image-library', label: 'Image library', icon: FileText },
      { id: 'original-files', label: 'Original files store', icon: Database },
      { id: 'orig-layout',    label: 'Original files page', icon: Layout },
      { id: 'parser',          label: 'Parser & catalog sync', icon: RefreshCw },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'access-time', label: 'Access time',   icon: Clock },
      { id: 'alerts-tickets', label: 'Ticket reminders', icon: Bell },
      { id: 'captcha',        label: 'Captcha (Turnstile)', icon: Shield },
      { id: 'hpt-proxy',      label: 'HPT proxy',     icon: Wrench },
      { id: 'data-tools',     label: 'Data tools',    icon: Database },
      { id: 'maintenance', label: 'Maintenance', icon: AlertTriangle },
    ],
  },
];
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings({ token, onNavigate, onProfileChanged }) {
  const [activeTab, setActiveTab] = useState('tuning');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', display_name: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [newOption, setNewOption] = useState({ name: '', display_name: '' });
  // "Show hidden" filter — when off we hide options that the admin has
  // unpublished (Hidden state). Default off so the table mirrors what a
  // customer sees in New Order.
  const [showHidden, setShowHidden] = useState(false);
  const [filter, setFilter] = useState('');

  const loadOptions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/tuning-options?published_only=false`);
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : [];
      if (!res.ok) throw new Error(data?.detail || 'Failed to load options');
      setOptions(data);
    } catch (e) {
      alert('Failed to load tuning options: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOptions(); }, []);

  const apiCall = async (path, method, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: body ? JSON.stringify(body) : undefined
    });
    const raw = await res.text();
    let data = null;
    try { data = raw ? JSON.parse(raw) : null; } catch (_) {}
    if (!res.ok) throw new Error(data?.detail || raw || `HTTP ${res.status}`);
    return data;
  };

  const handleAdd = async () => {
    if (!newOption.name.trim()) { alert('Option name is required'); return; }
    try {
      await apiCall('/api/pricing/tuning-options', 'POST', {
        name: newOption.name.trim(),
        display_name: newOption.display_name.trim() || newOption.name.trim(),
        published: true
      });
      setNewOption({ name: '', display_name: '' });
      setShowAdd(false);
      loadOptions();
    } catch (e) { alert('Create failed: ' + e.message); }
  };

  const startEdit = (opt) => {
    setEditingId(opt.id);
    setEditDraft({ name: opt.name, display_name: opt.display_name || opt.name });
  };

  const saveEdit = async (opt) => {
    try {
      await apiCall(`/api/pricing/tuning-options/${opt.id}`, 'PATCH', {
        name: editDraft.name,
        display_name: editDraft.display_name
      });
      setEditingId(null);
      loadOptions();
    } catch (e) { alert('Update failed: ' + e.message); }
  };

  const togglePublished = async (opt) => {
    try {
      await apiCall(`/api/pricing/tuning-options/${opt.id}`, 'PATCH', {
        published: !opt.published
      });
      loadOptions();
    } catch (e) { alert('Toggle failed: ' + e.message); }
  };

  /**
   * Flip an option's classification between engine ECU and gearbox
   * TCU. The customer's New Order Step 2 reads `option_type` and
   * groups every TCU-tagged row under a separate "Gearbox tuning
   * options:" subheading, mirroring the legacy site layout.
   */
  const setOptionType = async (opt, nextType) => {
    if (nextType === (opt.option_type || 'engine')) return;
    try {
      await apiCall(`/api/pricing/tuning-options/${opt.id}`, 'PATCH', {
        option_type: nextType,
      });
      loadOptions();
    } catch (e) { alert('Type change failed: ' + e.message); }
  };

  const move = async (opt, direction) => {
    const sorted = [...options].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(o => o.id === opt.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      await Promise.all([
        apiCall(`/api/pricing/tuning-options/${opt.id}`, 'PATCH', { order: other.order }),
        apiCall(`/api/pricing/tuning-options/${other.id}`, 'PATCH', { order: opt.order })
      ]);
      loadOptions();
    } catch (e) { alert('Reorder failed: ' + e.message); }
  };

  const handleDelete = async (opt) => {
    if (!opt.is_custom) {
      alert('Imported Excel options cannot be deleted. Toggle them to Hidden instead.');
      return;
    }
    if (!window.confirm(`Delete custom option "${opt.display_name || opt.name}"?\n\nThe server will refuse the delete if any price rows or orders reference it — you'll be asked to Hide it instead.`)) return;
    try {
      await apiCall(`/api/pricing/tuning-options/${opt.id}`, 'DELETE');
      loadOptions();
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const sortedOptions = [...options].sort((a, b) => a.order - b.order);
  // Apply local filters AFTER sort so the search input acts on display
  // labels regardless of order. `showHidden=false` removes unpublished
  // options so the table mirrors customer/admin-picker reality.
  const visibleOptions = sortedOptions.filter((o) => {
    if (!showHidden && !o.published) return false;
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (o.name || '').toLowerCase().includes(q)
        || (o.display_name || '').toLowerCase().includes(q)
        || (o.key || '').toLowerCase().includes(q);
  });
  const hiddenCount = options.filter((o) => !o.published).length;
  const publishedCount = options.filter(o => o.published).length;
  const customCount = options.filter(o => o.is_custom).length;

  return (
    <div className="p-3 sm:p-4 md:p-6 w-full lg:w-[80vw] mx-auto" data-testid="admin-settings-page">
      <div className="flex items-center gap-3 mb-4 md:mb-5 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow">
          <SettingsIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Settings</h1>
          <p className="text-sm text-slate-600">Manage configuration grouped by area.</p>
        </div>
        {/* Quick-access shortcuts to Customers and Prices (moved out of the
            main top nav to keep it compact). Hidden if `onNavigate` was not
            wired (e.g. when AdminSettings is rendered outside AdminPanel). */}
        {onNavigate && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => onNavigate('customers')}
              data-testid="settings-shortcut-customers"
              className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50 shadow-sm transition-all"
              title="Open Customers"
            >
              <Users className="w-4 h-4 text-cyan-600 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline text-sm font-medium text-slate-700 group-hover:text-cyan-700">Customers</span>
            </button>
            <button
              type="button"
              onClick={() => onNavigate('prices')}
              data-testid="settings-shortcut-prices"
              className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-violet-400 hover:bg-violet-50 shadow-sm transition-all"
              title="Open Prices Database"
            >
              <Database className="w-4 h-4 text-violet-600 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline text-sm font-medium text-slate-700 group-hover:text-violet-700">Prices</span>
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar — collapses to top scroll-strip on mobile.
            Slimmed down (w-52 / px-3 / smaller text) so the active
            section (Companies, Stripe, Invoice template…) gets ~50px
            more horizontal room — meaningful for the WYSIWYG editor
            and the side-by-side logo-placement live preview. */}
        <aside className="lg:w-52 flex-shrink-0 order-1" data-testid="settings-sidebar">
          {/* Mobile: native <select> grouped by area — one tap opens the
              OS picker with visual group headers (optgroup labels). Much
              faster to navigate than the flat horizontal scroll-strip
              that came before, which forced users to swipe through ~20
              chips just to find "Companies" or "Legal". */}
          <div className="lg:hidden mb-3">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Section</label>
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                data-testid="settings-mobile-select"
                className="w-full appearance-none pl-3 pr-9 py-2.5 text-sm font-semibold rounded-xl bg-white border border-slate-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 outline-none shadow-sm"
              >
                {SETTINGS_GROUPS.map((group) => (
                  <optgroup key={group.id} label={group.label}>
                    {group.items.map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronRight className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
            </div>
          </div>

          {/* Desktop: vertical grouped sidebar (compact mode) */}
          <div className="hidden lg:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {SETTINGS_GROUPS.map((group, gi) => (
              <div key={group.id} className={gi > 0 ? 'border-t border-slate-100' : ''}>
                <p className="px-3 pt-2.5 pb-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{group.label}</p>
                <div className="pb-1.5">
                  {group.items.map((item) => {
                    const active = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        data-testid={`settings-tab-${item.id}`}
                        className={`w-full flex items-center justify-between gap-1.5 px-3 py-1.5 text-[13px] transition-colors text-left ${
                          active
                            ? 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 font-bold border-l-2 border-cyan-500'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <item.icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-cyan-600' : 'text-slate-400'}`} />
                          <span className="truncate">{item.label}</span>
                        </span>
                        {active && <ChevronRight className="w-3 h-3 text-cyan-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Active section — wrapped in overflow-x-auto so wide editor
            tables (Tuning options, Bonuses, Price rules…) stay
            scrollable INSIDE the section on mobile instead of blowing
            out the whole page width. */}
        <section className="flex-1 min-w-0 order-2 overflow-x-auto" data-testid="settings-active-section">
          {activeTab === 'profile-photo' && <AdminProfilePhotoTab token={token} onProfileChanged={onProfileChanged} />}
          {activeTab === 'my-password' && <AdminPasswordTab token={token} />}
          {activeTab === 'configuration' && <AdminConfiguration token={token} />}
          {activeTab === 'working-hours' && <WorkingHoursEditor token={token} />}
          {activeTab === 'bonus' && <BonusPackagesEditor token={token} />}
          {activeTab === 'texts' && <ConfigTextsEditor token={token} />}
          {activeTab === 'reading-methods' && <ReadingMethodsEditor token={token} />}
          {activeTab === 'gearbox-types' && <GearboxTypesEditor token={token} />}
          {activeTab === 'legal' && <LegalDocsEditor token={token} />}
          {activeTab === 'email-sender' && <EmailSenderEditor token={token} />}
          {activeTab === 'price-multiplier' && <PriceMultiplierEditor token={token} />}
          {activeTab === 'email' && <EmailTemplatesEditor token={token} />}
          {activeTab === 'companies' && <CompaniesEditor token={token} />}
          {activeTab === 'customer-badges' && <CustomerBadgeSettings token={token} />}
          {activeTab === 'security-blocklist' && <SecurityBlocklistPanel token={token} />}
          {activeTab === 'risk-limits' && <RiskLimitsPanel token={token} />}
          {activeTab === 'groups' && <CustomerGroupsEditor token={token} />}
          {activeTab === 'disable' && <ServiceFlagsEditor token={token} />}
          {activeTab === 'price-hours' && <PriceHoursEditor token={token} />}
          {activeTab === 'pins' && <PinCodesEditor token={token} />}
          {activeTab === 'news' && <AdminNewsEditor token={token} />}
          {activeTab === 'stripe' && <StripeSettingsEditor token={token} />}
          {activeTab === 'login-banner' && <LoginBannerEditor token={token} />}
          {activeTab === 'signup-tip' && <SignupEmailTipEditor token={token} />}
          {activeTab === 'recharge-limits' && <RechargeLimitsEditor token={token} />}
          {activeTab === 'invoice-template' && <InvoiceTemplateEditor token={token} />}
          {activeTab === 'welcome-bonus' && <WelcomeBonusEditor token={token} />}
          {activeTab === 'sounds' && <SoundsEditor token={token} />}
          {activeTab === 'image-library' && <InstructionsLibraryEditor token={token} />}
          {activeTab === 'original-files' && <OriginalFilesAdmin token={token} />}
          {activeTab === 'orig-layout' && <OriginalFilesLayoutEditor token={token} />}
      {activeTab === 'parser' && <AdminParser token={token} />}
      {activeTab === 'maintenance' && <AdminMaintenance token={token} />}
      {activeTab === 'access-time' && <AccessTimeEditor token={token} />}
      {activeTab === 'alerts-tickets' && <TicketSlaAlertEditor token={token} />}
          {activeTab === 'captcha' && (
            <>
              <CaptchaSettingsEditor token={token} />
              <CaptchaStats token={token} />
            </>
          )}
          {activeTab === 'hpt-proxy' && <HptProxySettings token={token} />}
          {activeTab === 'data-tools' && <DataToolsSettings token={token} />}

          {activeTab === 'tuning' && (
        <>
          {/* Stats — smaller padding on mobile so 3 rows fit on 1 screen */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="bg-white p-3 md:p-5 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs md:text-sm text-slate-600">Total Options</p>
              <p className="text-2xl md:text-3xl font-bold text-slate-800 mt-1">{options.length}</p>
            </div>
            <div className="bg-white p-3 md:p-5 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs md:text-sm text-slate-600">Published (visible to users)</p>
              <p className="text-2xl md:text-3xl font-bold text-emerald-600 mt-1">{publishedCount}</p>
            </div>
            <div className="bg-white p-3 md:p-5 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs md:text-sm text-slate-600">Custom (user-added)</p>
              <p className="text-2xl md:text-3xl font-bold text-cyan-600 mt-1">{customCount}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div>
              <h2 className="font-bold text-slate-800">Tuning Options Catalog</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                These names appear in the user order wizard. Built-in options come from the imported Excel. Custom options are managed here.
                {hiddenCount > 0 && (
                  <span className="ml-1 text-slate-600">
                    · <span className="font-mono">{hiddenCount}</span> hidden
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search filter */}
              <input
                type="text"
                placeholder="Filter by name / key…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="tuning-options-filter"
                className="h-9 px-3 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 outline-none w-full sm:w-56 min-w-0"
              />
              {/* Show hidden toggle */}
              <label className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                  data-testid="tuning-options-show-hidden"
                  className="accent-cyan-600"
                />
                <span className="text-xs font-bold text-slate-700">Show hidden</span>
                {hiddenCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-bold ml-0.5">{hiddenCount}</span>
                )}
              </label>
              <button
                onClick={() => setShowAdd(true)}
                data-testid="add-tuning-option-btn"
                className="h-9 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Add Custom Option
              </button>
            </div>
          </div>

          {/* Add form */}
          {showAdd && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-5 mb-4" data-testid="add-option-form">
              <h3 className="font-bold text-slate-800 mb-3">Create Custom Tuning Option</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Internal Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Custom Launch Control"
                    value={newOption.name}
                    onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                    data-testid="new-option-name-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Display Name (optional)</label>
                  <input
                    type="text"
                    placeholder="Shown to users"
                    value={newOption.display_name}
                    onChange={(e) => setNewOption({ ...newOption, display_name: e.target.value })}
                    data-testid="new-option-display-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAdd}
                  data-testid="save-new-option-btn"
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
                <button
                  onClick={() => { setShowAdd(false); setNewOption({ name: '', display_name: '' }); }}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium flex items-center gap-2"
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[750px]">
                <thead>
                  <tr className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white text-xs font-bold uppercase">
                    <th className="px-4 py-3 text-left w-20">Order</th>
                    <th className="px-4 py-3 text-left">Internal Name</th>
                    <th className="px-4 py-3 text-left">Display Name</th>
                    <th className="px-4 py-3 text-center w-36" title="Engine ECU options render in the main list. TCU options group under a 'Gearbox tuning options' subheading on New Order Step 2.">Type</th>
                    <th className="px-4 py-3 text-center w-32">Source</th>
                    <th className="px-4 py-3 text-center w-32">Published</th>
                    <th className="px-4 py-3 text-center w-48">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="tuning-options-table-body">
                  {loading ? (
                    <tr><td colSpan="7" className="text-center py-10 text-slate-600">Loading...</td></tr>
                  ) : visibleOptions.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-10 text-slate-600">
                      {options.length === 0 ? 'No tuning options yet. Import an Excel file or add a custom option.'
                       : !showHidden && hiddenCount > 0 ? `No visible options match. Toggle "Show hidden" to reveal ${hiddenCount} hidden one${hiddenCount === 1 ? '' : 's'}.`
                       : 'No options match the current filter.'}
                    </td></tr>
                  ) : (
                    visibleOptions.map((opt, idx) => (
                      <tr key={opt.id} className={`border-b border-slate-100 hover:bg-cyan-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${!opt.published ? 'opacity-60' : ''}`} data-testid={`option-row-${opt.key}`}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-mono text-slate-600 w-8">{opt.order}</span>
                            <button onClick={() => move(opt, 'up')} disabled={idx === 0} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                            <button onClick={() => move(opt, 'down')} disabled={idx === visibleOptions.length - 1} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-slate-800">
                          {editingId === opt.id ? (
                            <input
                              value={editDraft.name}
                              onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                              className="w-full px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          ) : (
                            <span className="font-medium">{opt.name}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-slate-700">
                          {editingId === opt.id ? (
                            <input
                              value={editDraft.display_name}
                              onChange={(e) => setEditDraft({ ...editDraft, display_name: e.target.value })}
                              className="w-full px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          ) : (
                            opt.display_name || opt.name
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <select
                            value={opt.option_type || 'engine'}
                            onChange={(e) => setOptionType(opt, e.target.value)}
                            data-testid={`option-type-${opt.key}`}
                            title="Engine ECU options stay in the main list on New Order Step 2. TCU options group under a 'Gearbox tuning options:' subheading below."
                            className={`text-[11px] font-semibold rounded px-2 py-1 border focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                              (opt.option_type || 'engine') === 'tcu'
                                ? 'bg-violet-50 border-violet-300 text-violet-800'
                                : 'bg-slate-50 border-slate-300 text-slate-700'
                            }`}
                          >
                            <option value="engine">Engine ECU</option>
                            <option value="tcu">TCU (Gearbox)</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {opt.is_custom ? (
                            <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs font-bold">CUSTOM</span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-200 text-slate-700 rounded text-xs font-bold inline-flex items-center gap-1"><Lock className="w-3 h-3" /> EXCEL</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => togglePublished(opt)}
                            data-testid={`toggle-published-${opt.key}`}
                            title={opt.published
                              ? 'Currently VISIBLE. Click to hide — option disappears from New Order Step 2 and Price Catalog dropdown.'
                              : 'Currently HIDDEN. Click to restore — option reappears in customer / admin pickers.'}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors inline-flex items-center gap-1 ${
                              opt.published
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            }`}
                          >
                            {opt.published ? <><Eye className="w-3 h-3" /> Visible</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {editingId === opt.id ? (
                            <div className="flex gap-1 justify-center">
                              <button onClick={() => saveEdit(opt)} className="p-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded"><Save className="w-4 h-4" /></button>
                              <button onClick={() => setEditingId(null)} className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded"><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-center">
                              <button onClick={() => startEdit(opt)} data-testid={`edit-option-${opt.key}`} className="p-1.5 bg-slate-100 hover:bg-cyan-100 text-cyan-700 rounded" title="Rename"><Edit2 className="w-4 h-4" /></button>
                              <button
                                onClick={() => handleDelete(opt)}
                                disabled={!opt.is_custom}
                                data-testid={`delete-option-${opt.key}`}
                                title={opt.is_custom
                                  ? 'Delete (only allowed when no price rows / orders reference this option — otherwise use Hide)'
                                  : 'Imported Excel option — use Hide instead of Delete'}
                                className="p-1.5 bg-slate-100 hover:bg-red-100 text-red-600 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-4 text-xs text-slate-500">
            <strong>Note:</strong> Built-in options inherit their prices from the imported Excel (per vehicle/ECU). Custom options are tracked separately and can be used for global add-ons that are not in the Excel (e.g. consultation fees, global add-ons).
          </p>
        </>
      )}
        </section>
      </div>
    </div>
  );
}


// Module level: defining this inside AdminPasswordTab would remount the
// input on every keystroke and drop focus.
const PwdField = ({ label, value, onChange, testid, autoComplete, show }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 mb-1">{label} *</label>
    <input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
    />
  </div>
);

/**
 * AdminPasswordTab — "My account → Password".
 * Requires the CURRENT password (a stolen session alone must not be
 * able to take the account over), signs every other session out and
 * swaps in the fresh token the backend returns.
 */
function AdminPasswordTab({ token }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!cur || !next || !confirm) { setMsg({ type: 'error', text: 'Fill in all three fields' }); return; }
    if (next !== confirm) { setMsg({ type: 'error', text: 'New passwords do not match' }); return; }
    if (next.length < 10) { setMsg({ type: 'error', text: 'New password must be at least 10 characters' }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/me/password/change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: cur, new_password: next, confirm_password: confirm }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg({ type: 'error', text: data.detail || 'Could not change the password' }); return; }
      // Old tokens are dead — persist the fresh one before reloading.
      const store = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
      if (data.access_token) store.setItem('auth_token', data.access_token);
      setCur(''); setNext(''); setConfirm('');
      setMsg({ type: 'success', text: 'Password changed. Other sessions were signed out — reloading…' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (_) {
      setMsg({ type: 'error', text: 'Network error — try again' });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-xl" data-testid="admin-password-tab">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <h2 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-600" /> Change my password
        </h2>
        <p className="text-xs text-slate-500">
          Your current password is required. After the change, every other
          session of this account is signed out and a notification e-mail is
          sent to {''}you. The change is recorded in Security → Action log.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-3">
        <PwdField show={show} label="Current password" value={cur} onChange={setCur}
          testid="admin-password-current" autoComplete="current-password" />
        <PwdField show={show} label="New password (min 10 characters)" value={next} onChange={setNext}
          testid="admin-password-new" autoComplete="new-password" />
        <PwdField show={show} label="Confirm new password" value={confirm} onChange={setConfirm}
          testid="admin-password-confirm" autoComplete="new-password" />

        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)}
            data-testid="admin-password-show" />
          Show passwords
        </label>

        {msg && (
          <div data-testid="admin-password-msg"
            className={`text-xs font-semibold px-3 py-2 rounded-lg ${
              msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
            {msg.text}
          </div>
        )}

        <button type="submit" disabled={busy} data-testid="admin-password-submit"
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2">
          <Save className="w-4 h-4" /> {busy ? 'Saving…' : 'Change password'}
        </button>
      </form>
    </div>
  );
}


/**
 * AdminProfilePhotoTab — admin "My account → Profile photo" panel.
 * Loads the admin's own profile from `/api/auth/me` and wraps the
 * shared `<AvatarUploadCard>` so the same UX works for both clients
 * (MyProfile) and admins (this tab).
 */
function AdminProfilePhotoTab({ token, onProfileChanged }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (alive) setProfile(data);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!profile) return <div className="text-sm text-rose-700">Could not load profile.</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <h2 className="text-base font-bold text-slate-800 mb-1">My account</h2>
        <p className="text-xs text-slate-500">
          Your profile photo appears in the header and next to your messages in
          customer chats. Use a clear, square photo for best results.
        </p>
      </div>
      <AvatarUploadCard
        token={token}
        avatarUrl={profile.avatar_url || null}
        name={`${profile.first_name || ''} ${profile.surname || ''}`.trim()}
        email={profile.email}
        isAdmin
        onChange={(newUrl) => {
          setProfile(p => ({ ...p, avatar_url: newUrl }));
          // Bubble up so the admin header avatar refreshes immediately.
          onProfileChanged?.();
        }}
        testidPrefix="admin-profile-avatar"
      />
    </div>
  );
}
